import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ListMusic, Users, Disc3, Clock3, ArrowRight, DoorOpen, Heart, Cake, Wine } from "lucide-react";
import { Reveal } from "./Reveal";

const STEPS = [
  { icon: ListMusic, title: "Vous recevez votre accès", text: "Un lien vers votre playlist collaborative, à partager avec vos proches." },
  { icon: Users, title: "Chacun propose ses titres", text: "Vos invités suggèrent leurs morceaux, vous validez ce qui compte vraiment." },
  { icon: Disc3, title: "Nous orchestrons le tout", text: "Nous intégrons vos choix à une programmation cohérente et dansante." },
];

const MOMENTS = [
  { icon: DoorOpen, label: "Entrée des mariés" },
  { icon: Heart, label: "Ouverture du bal" },
  { icon: Cake, label: "Musique du gâteau" },
  { icon: Wine, label: "Fond sonore apéritif & repas" },
];

export default function Playlist() {
  return (
    <section id="playlist" className="relative overflow-hidden py-28 sm:py-36" data-testid="playlist-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <div>
          <Reveal>
            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-cyan-400">La playlist collaborative</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl lg:text-5xl">
              Votre soirée <span className="text-gradient">sonne comme vous.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 text-base leading-relaxed text-slate-400 sm:text-lg">
              Avant votre événement, vous recevez un accès à notre outil de playlist collaborative :
              vous et vos proches proposez vos titres, nous les intégrons à la programmation. C'est
              l'outil essentiel pour une soirée qui vous ressemble vraiment.
            </p>
          </Reveal>

          <div className="mt-10 space-y-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={0.2 + i * 0.1}>
                <div className="flex items-start gap-4" data-testid={`playlist-step-${i + 1}`}>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-cyan-400/25 bg-cyan-400/10">
                    <s.icon size={20} className="text-cyan-300" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-slate-100">{s.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-400">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.45}>
            <div className="mt-10" data-testid="playlist-key-moments">
              <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-cyan-400">
                Les souhaits des mariés, moment par moment
              </p>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {MOMENTS.map((m) => (
                  <div
                    key={m.label}
                    className="flex items-center gap-3 rounded-xl border border-cyan-500/15 bg-[#0E1524]/70 px-4 py-3 transition-colors duration-300 hover:border-cyan-400/40"
                    data-testid={`playlist-moment-${m.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  >
                    <m.icon size={17} className="shrink-0 text-cyan-300" />
                    <span className="text-sm font-semibold text-slate-200">{m.label}</span>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-slate-400">
                Pour chacun de ces moments, vous choisissez votre titre dans la playlist
                collaborative : nous le préparons et le lançons à la seconde près.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.5}>
            <div
              className="mt-10 flex items-start gap-4 rounded-2xl border border-cyan-400/30 bg-gradient-to-r from-cyan-500/10 to-blue-600/10 p-5"
              data-testid="playlist-deadline-callout"
            >
              <Clock3 size={22} className="mt-0.5 shrink-0 text-cyan-300" />
              <p className="text-sm leading-relaxed text-slate-200">
                <span className="font-bold text-cyan-300">Demandes clôturées 48 h avant le mariage.</span>{" "}
                Passé ce délai, la playlist est verrouillée : c'est notre garantie de transitions
                fluides et d'une soirée sans fausse note.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.6}>
            <Link
              to="/playlists"
              data-testid="playlist-cta-espace"
              className="group mt-8 inline-flex items-center gap-2 rounded-full border border-cyan-400/30 px-6 py-3 text-sm font-semibold text-cyan-300 transition-colors duration-300 hover:border-cyan-400/60 hover:bg-cyan-400/10"
            >
              Accéder à l'espace playlists
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="relative">
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-3xl border border-cyan-400/20"
            data-testid="playlist-visual"
          >
            <img
              src="https://images.unsplash.com/photo-1603048588665-791ca8aea617?w=1100&q=80&auto=format&fit=crop"
              alt="Platine vinyle — ambiance musicale"
              className="aspect-[4/5] w-full object-cover sm:aspect-square"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#080C14]/80 via-transparent to-[#080C14]/20" />
            <div className="absolute bottom-6 left-6 right-6 rounded-2xl border border-cyan-400/20 bg-[#0E1524]/80 p-5 backdrop-blur-xl">
              <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-cyan-400">En direct de la régie</p>
              <p className="mt-1.5 text-sm font-semibold text-slate-100">
                Vos titres, notre programmation : la bonne chanson, au bon moment.
              </p>
            </div>
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
}
